import {
    PiBarcodeBold,
    PiCreditCardBold,
    PiMoneyBold,
    PiQrCodeBold,
} from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

export type PaymentMethodKind = 'pix' | 'boleto' | 'cartao' | 'other' | null | undefined;

type Props = {
    label?: string | null;
    kind?: PaymentMethodKind;
    size?: 'sm' | 'md';
    showLabel?: boolean;
    className?: string;
};

export function normalizePaymentMethodKind(label?: string | null): PaymentMethodKind {
    if (!label?.trim()) return null;
    const v = label.trim().toLowerCase();
    if (v.includes('pix')) return 'pix';
    if (v.includes('boleto')) return 'boleto';
    if (
        v.includes('cart') ||
        v.includes('crédito') ||
        v.includes('credito') ||
        v.includes('credit') ||
        v.includes('débito') ||
        v.includes('debito')
    ) {
        return 'cartao';
    }
    return 'other';
}

const KIND_META: Record<
    Exclude<PaymentMethodKind, null | undefined>,
    {
        Icon: React.ComponentType<{ className?: string }>;
        tone: string;
        fallbackLabel: string;
    }
> = {
    pix: {
        Icon: PiQrCodeBold,
        tone: 'bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/60',
        fallbackLabel: 'PIX',
    },
    boleto: {
        Icon: PiBarcodeBold,
        tone: 'bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800/60',
        fallbackLabel: 'Boleto',
    },
    cartao: {
        Icon: PiCreditCardBold,
        tone: 'bg-sky-100 text-sky-900 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:ring-sky-800/60',
        fallbackLabel: 'Cartão',
    },
    other: {
        Icon: PiMoneyBold,
        tone: 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600',
        fallbackLabel: 'Outro',
    },
};

export default function PaymentMethodBadge({
    label,
    kind,
    size = 'md',
    showLabel = true,
    className,
}: Props) {
    const resolvedKind = kind ?? normalizePaymentMethodKind(label);
    if (!resolvedKind && !label?.trim()) {
        return <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>;
    }

    const meta = KIND_META[resolvedKind ?? 'other'];
    const Icon = meta.Icon;
    const text = label?.trim() || meta.fallbackLabel;
    const iconSize = size === 'sm' ? 'size-3.5' : 'size-4';
    const pad = size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';

    return (
        <span
            className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-full font-semibold ring-1',
                pad,
                meta.tone,
                className,
            )}
            title={text}
        >
            <Icon className={cn(iconSize, 'shrink-0')} aria-hidden />
            {showLabel && <span className="truncate">{text}</span>}
        </span>
    );
}
